import { type StaticDecode, Type } from 'typebox';
import { LocString, ReadonlyNullable } from './schema';
import { Image, MongoAndPgEntity } from './shared';

export const Role = Type.Union([
  Type.Literal('external'),
  Type.Literal('limited_access'),
  Type.Literal('consultant'),
  Type.Literal('countrymanager'),
  Type.Literal('referencemanager'),
  Type.Literal('internationalmanager'),
  Type.Literal('departmentmanager'),
  // the below are for API users
  Type.Literal('proposal'),
  Type.Literal('readonly_consultant'),
  Type.Literal('readonly_countrymanager'),
  Type.Literal('readonly_internationalmanager'),
  Type.Literal('readonly_proposal'),
]);
export type Role = StaticDecode<typeof Role>;

const BaseUser = Type.Object({
  company_id: Type.Readonly(Type.String()),
  office_id: ReadonlyNullable(Type.String()),
  country_id: ReadonlyNullable(Type.String()),
  country_code: ReadonlyNullable(Type.String()),
  role: Type.Readonly(Role),
  roles: Type.Readonly(Type.Array(Role)),
  created_at: Type.Readonly(Type.String()),
});

export const User = Type.Interface([MongoAndPgEntity, BaseUser], {
  user_id: Type.Readonly(Type.String()),
  name: Type.Readonly(Type.String()),
  external_unique_id: ReadonlyNullable(Type.String()),
  company_name: Type.Readonly(Type.String()),
  email: Type.Readonly(Type.String()),
  telephone: ReadonlyNullable(Type.String()),
  language_code: Type.Readonly(Type.String()),
  language_codes: Type.Readonly(Type.Array(Type.String())),
  ui_language_code: Type.Readonly(Type.String()),
  company_group_ids: Type.Readonly(Type.Array(Type.String())),
  masterdata_languages: Type.Readonly(Type.Array(Type.String())),
  override_language_code: ReadonlyNullable(Type.String()),
  default_cv_id: ReadonlyNullable(Type.String()),
  default_cv_template_id: ReadonlyNullable(Type.String()),
  office_name: ReadonlyNullable(Type.String()),
  company_subdomains: Type.Readonly(Type.Array(Type.String())),
  role_allowed_office_ids: ReadonlyNullable(Type.Array(Type.String())),
  role_allowed_tag_ids: ReadonlyNullable(Type.Array(Type.String())),
  selected_tag_ids: Type.Readonly(Type.Array(Type.String())),
  selected_office_ids: Type.Readonly(Type.Array(Type.String())),
  international_toggle: Type.Readonly(
    Type.Union([Type.Literal('expand'), Type.Literal('hide')]),
  ),
  include_officeless_reference_projects: Type.Readonly(Type.Boolean()),
  expand_proposals_toggle: Type.Readonly(Type.Boolean()),
  preferred_download_format: Type.Readonly(Type.String()),
  deactivated: Type.Readonly(Type.Boolean()),
  deactivated_at: Type.Readonly(
    // `false` if not deactivated, otherwise timestamp
    Type.Union([Type.Literal(false), Type.String()]),
  ),
  image: Type.Readonly(Image),
  is_authenticated_by_password: Type.Readonly(Type.Boolean()),
  upn: ReadonlyNullable(Type.String()),
  title: Type.Readonly(LocString),
  updated_at: Type.Readonly(Type.String()),
});
export type User = StaticDecode<typeof User>;

export const ApiUser = Type.Interface([MongoAndPgEntity, BaseUser], {
  name: ReadonlyNullable(Type.String()),
  technical_contact: ReadonlyNullable(Type.String()),
  access_token: Type.Readonly(Type.String()),
  enabled: Type.Readonly(Type.Boolean()),
  created_by: ReadonlyNullable(Type.String()),
});
export type ApiUser = StaticDecode<typeof ApiUser>;

export type UserOrApiUser = User | ApiUser;

export function isApiUser(user: UserOrApiUser): user is ApiUser {
  return typeof (user as ApiUser).access_token === 'string';
}
